/**
 * NetworkConfigModal — DEVICE-DETAIL-MANAGE-1E2-NETWORK.
 *
 * Configura o modo de rede (DHCP ou IP estático) do device a partir da página
 * Detalhes, usando o endpoint JÁ existente e guardado:
 *   POST /api/admin/devices/{id}/config/network  (configNetwork em api.js)
 *
 * SEPARADO do WiFi de propósito (endpoints distintos): mudar IP não toca o Wi-Fi e
 * vice-versa. Backend = fonte da verdade dos guards: CAP_DEVICES_CONFIG (botão gated)
 * + confirmação forte SEMPRE (S3.10C1). NÃO cria comando fora deste endpoint.
 *
 * Contrato (ConfigNetworkIn): dhcp (bool). Se dhcp=true → NENHUM outro campo pode ir
 * (o backend rejeita). Se dhcp=false → ip/mask/gateway/dns1 obrigatórios (IPv4) +
 * dns2 opcional. Copy honesta: config enviada como PENDENTE, aplica no próximo
 * heartbeat/ACK (não imediato).
 */
import React, { useEffect, useState } from "react";

import { configNetwork, getDeviceConfigSnapshot } from "../lib/api.js";
import { useCriticalConfirm } from "./ConfirmCriticalAction.jsx";

const INPUT =
  "w-full bg-noc-bg border border-noc-border rounded-md px-2 py-1.5 " +
  "text-[13px] text-noc-text focus:border-noc-accent outline-none disabled:opacity-40";

function isIPv4(s) {
  const v = String(s || "").trim();
  const m = v.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false;
  return m.slice(1).every((o) => {
    const n = Number(o);
    return n >= 0 && n <= 255 && String(n) === String(Number(o));
  });
}

function friendlyErr(e) {
  const s = e?.status;
  if (s === 401) return "Sessão expirada — faça login novamente.";
  if (s === 403) return "Sem permissão (devices:config) ou confirmação recusada.";
  if (s === 404) return "Device não encontrado.";
  if (s === 422) return `Dado inválido: ${e?.message || "verifique os campos."}`;
  return e?.message || "Falha ao configurar a rede.";
}

export default function NetworkConfigModal({ deviceId, device, onClose, onSaved }) {
  const confirmCritical = useCriticalConfirm();
  const protectedDev = !!device?.ota_protected;

  const [dhcp, setDhcp] = useState(true);
  const [ip, setIp] = useState("");
  const [mask, setMask] = useState("");
  const [gateway, setGateway] = useState("");
  const [dns1, setDns1] = useState("");
  const [dns2, setDns2] = useState("");

  const [initial, setInitial] = useState(null);   // snapshot.network (ou null se desconhecido)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    getDeviceConfigSnapshot(deviceId)
      .then((r) => {
        if (!alive) return;
        const n = r?.last_config_snapshot?.network;
        if (!n) return;
        const norm = {
          dhcp: n.dhcp !== false,
          ip: n.ip || "", mask: n.mask || "", gateway: n.gateway || "",
          dns1: n.dns1 || "", dns2: n.dns2 || "",
        };
        setInitial(norm);
        setDhcp(norm.dhcp);
        setIp(norm.ip); setMask(norm.mask); setGateway(norm.gateway);
        setDns1(norm.dns1); setDns2(norm.dns2);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [deviceId]);

  const changed = !initial || (
    dhcp !== initial.dhcp ||
    (!dhcp && (ip !== initial.ip || mask !== initial.mask || gateway !== initial.gateway
      || dns1 !== initial.dns1 || dns2 !== initial.dns2))
  );

  function validate() {
    if (dhcp) return null;
    for (const [label, val] of [["IP", ip], ["Máscara", mask], ["Gateway", gateway], ["DNS 1", dns1]]) {
      if (!String(val).trim()) return `${label} é obrigatório no modo IP estático.`;
      if (!isIPv4(val)) return `${label} inválido (IPv4 esperado, ex.: 192.168.0.10).`;
    }
    if (dns2.trim() && !isIPv4(dns2)) return "DNS 2 inválido (IPv4).";
    return null;
  }

  async function handleSave() {
    if (!changed) { onClose(); return; }
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    const secret = await confirmCritical({
      actionLabel: dhcp ? "Configurar rede: DHCP" : "Configurar rede: IP estático",
      target: { kind: "single", deviceId },
      impact: protectedDev
        ? "Device PROTEGIDO. IP fora da rota pode tornar o device INALCANÇÁVEL."
        : "IP fora da rota pode tornar o device INALCANÇÁVEL (aplica no próximo heartbeat).",
    });
    if (secret == null) return;
    setSaving(true);
    try {
      // dhcp=true → SÓ {dhcp:true} (o backend rejeita campos extras em DHCP).
      const body = dhcp
        ? { dhcp: true }
        : {
            dhcp: false,
            ip: ip.trim(), mask: mask.trim(), gateway: gateway.trim(),
            dns1: dns1.trim(), dns2: dns2.trim() || null,
          };
      await configNetwork(deviceId, body, { confirm: secret });
      onSaved && onSaved();
    } catch (e) {
      setError(friendlyErr(e));
      setSaving(false);
    }
  }

  const ipField = (label, val, set, ph, required) => (
    <div className="space-y-1">
      <label className="text-[11px] uppercase tracking-wide text-noc-faint">
        {label}{required ? "" : " (opcional)"}
      </label>
      <input className={INPUT} inputMode="decimal" placeholder={ph} value={val}
        disabled={dhcp} onChange={(e) => set(e.target.value)} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={saving ? undefined : onClose}>
      <div className="bg-noc-panel border border-noc-border rounded-xl w-full max-w-lg
                      max-h-[90vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-noc-text">Configurar IP / Rede</h2>
          <span className="font-mono text-[11px] text-noc-faint">{deviceId}</span>
        </div>

        <div className="text-[12px] bg-noc-warn/10 border border-noc-warn/30 text-noc-warn
                        rounded-md px-3 py-2">
          ⚠ IP estático fora da subnet/rota pode deixar o device <b>inalcançável</b>. A
          config é enviada como <b>pendente</b> e aplicada no próximo heartbeat (não é
          imediato).{protectedDev && " 🔒 Device PROTEGIDO (produção/gold/canário)."}
        </div>

        {error && (
          <div className="text-[12px] bg-noc-err/10 border border-noc-err/30 text-noc-err
                          rounded-md px-3 py-2">{error}</div>
        )}

        {/* modo */}
        <div className="space-y-1">
          <label className="text-[11px] uppercase tracking-wide text-noc-faint">Modo</label>
          <select className={INPUT} value={dhcp ? "dhcp" : "static"}
            onChange={(e) => setDhcp(e.target.value === "dhcp")}>
            <option value="dhcp">DHCP (automático)</option>
            <option value="static">IP estático</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {ipField("IP", ip, setIp, "192.168.0.10", true)}
          {ipField("Máscara", mask, setMask, "255.255.255.0", true)}
          {ipField("Gateway", gateway, setGateway, "192.168.0.1", true)}
          {ipField("DNS 1", dns1, setDns1, "8.8.8.8", true)}
          {ipField("DNS 2", dns2, setDns2, "1.1.1.1", false)}
        </div>
        {dhcp && (
          <div className="text-[11px] text-noc-faint">
            No modo DHCP os campos de IP estático são ignorados.
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || !changed}>
            {saving ? "Enviando…" : "Enviar ao device"}
          </button>
        </div>
      </div>
    </div>
  );
}
